import { useState, useEffect } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Car, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const CONTRACT_TYPE_OPTIONS = [
  { value: "LLD", label: "LLD — Location Longue Durée" },
  { value: "LOA", label: "LOA — Location avec Option d'Achat" },
  { value: "credit_bail", label: "Crédit-bail" },
  { value: "assurance", label: "Assurance auto" },
  { value: "maintenance", label: "Maintenance / Entretien" },
  { value: "achat", label: "Achat" },
  { value: "autre", label: "Autre" },
];

const ENERGY_OPTIONS = ["essence", "diesel", "electrique", "hybride", "gpl", "hydrogene", "autre"];

/**
 * Dialog de validation véhicule affiché après OCR si is_vehicle_contract=true.
 * Permet à l'utilisateur de vérifier/modifier les champs détectés avant de créer
 * le véhicule et son contrat dans le Parc Auto.
 */
export default function VehicleValidationDialog({
  open,
  onOpenChange,
  engagementId,
  extractedVehicleInfo,
  extractedContractInfo,
  token,
  onLinked,
  onSkip,
}) {
  const [vehicleData, setVehicleData] = useState({});
  const [contractData, setContractData] = useState({});
  const [loading, setLoading] = useState(false);
  const [existingVehicles, setExistingVehicles] = useState([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState("");

  useEffect(() => {
    if (!open) return;
    setVehicleData(extractedVehicleInfo || {});
    setContractData(extractedContractInfo || {});
    setSelectedVehicleId("");
    // Cherche un véhicule existant avec la même immatriculation
    const fetchVehicles = async () => {
      try {
        const res = await axios.get(`${API}/api/fleet/vehicles`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const list = res.data?.data || res.data?.vehicles || (Array.isArray(res.data) ? res.data : []);
        setExistingVehicles(list);
        // Match automatique par immatriculation
        const immat = (extractedVehicleInfo?.immatriculation || "").toUpperCase().replace(/[-\s]/g, "");
        if (immat) {
          const match = list.find(v => {
            const vImmat = (v.registration || v.license_plate || "").toUpperCase().replace(/[-\s]/g, "");
            return vImmat && vImmat === immat;
          });
          if (match) setSelectedVehicleId(match.id);
        }
      } catch (e) {
        // pas grave si l'appel échoue
      }
    };
    fetchVehicles();
  }, [open, extractedVehicleInfo, extractedContractInfo, token]);

  const updateVehicle = (field, value) => setVehicleData(p => ({ ...p, [field]: value }));
  const updateContract = (field, value) => setContractData(p => ({ ...p, [field]: value }));

  const toNum = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

  const handleConfirm = async () => {
    setLoading(true);
    try {
      const payload = {
        vehicle_id: selectedVehicleId || undefined,
        vehicle_data: selectedVehicleId ? undefined : {
          immatriculation: vehicleData.immatriculation || null,
          marque: vehicleData.marque || null,
          modele: vehicleData.modele || null,
          version: vehicleData.version || null,
          annee: toNum(vehicleData.annee),
          vin: vehicleData.vin || null,
          kilometrage: toNum(vehicleData.kilometrage),
          energie: vehicleData.energie || null,
          puissance_fiscale: toNum(vehicleData.puissance_fiscale),
          date_mise_en_circulation: vehicleData.date_mise_en_circulation || null,
        },
        contract_data: {
          type_contrat_auto: contractData.type_contrat_auto || null,
          loueur_ou_assureur: contractData.loueur_ou_assureur || null,
          duree_mois: toNum(contractData.duree_mois),
          monthly_rent_ht: toNum(contractData.monthly_rent_ht),
          monthly_rent_ttc: toNum(contractData.monthly_rent_ttc),
          depot_garantie: toNum(contractData.depot_garantie),
          valeur_rachat: toNum(contractData.valeur_rachat),
          km_total_autorise: toNum(contractData.km_total_autorise),
          km_annuel_autorise: toNum(contractData.km_annuel_autorise),
          cout_km_supplementaire: toNum(contractData.cout_km_supplementaire),
          franchise_restitution: toNum(contractData.franchise_restitution),
          franchise_sinistre: toNum(contractData.franchise_sinistre),
          plafond_indemnisation: toNum(contractData.plafond_indemnisation),
        },
      };
      const res = await axios.post(
        `${API}/api/engagements/contrats/${engagementId}/link-vehicle`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.created_new_vehicle
        ? "Véhicule créé dans Parc Auto et lié à l'engagement"
        : "Contrat véhicule ajouté au véhicule existant");
      onLinked?.(res.data);
      onOpenChange(false);
    } catch (e) {
      console.error("Link vehicle error:", e);
      toast.error(e.response?.data?.detail || "Erreur lors de la liaison au Parc Auto");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="vehicle-validation-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Car className="w-5 h-5 text-blue-600" />
            Véhicule détecté par l&apos;IA
          </DialogTitle>
          <DialogDescription>
            L&apos;analyse a détecté qu&apos;il s&apos;agit d&apos;un contrat véhicule. Vérifie et complète les
            informations ci-dessous, puis valide pour le créer automatiquement dans le module <strong>Parc Auto</strong>.
          </DialogDescription>
        </DialogHeader>

        {/* Sélection véhicule existant ou création */}
        <div className="space-y-1 p-3 bg-blue-50 border border-blue-100 rounded-lg">
          <Label className="text-xs font-medium text-blue-900 flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> Lier à un véhicule existant ou créer un nouveau ?
          </Label>
          <Select value={selectedVehicleId || "__new__"} onValueChange={(v) => setSelectedVehicleId(v === "__new__" ? "" : v)}>
            <SelectTrigger data-testid="select-existing-vehicle">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__new__">➕ Créer un nouveau véhicule</SelectItem>
              {existingVehicles.map(v => (
                <SelectItem key={v.id} value={v.id}>
                  🚗 {v.registration || v.license_plate || "Sans immatriculation"} — {v.brand} {v.model}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Section Véhicule (masquée si sélection véhicule existant) */}
        {!selectedVehicleId && (
          <div className="space-y-3">
            <h4 className="font-semibold text-sm text-foreground border-b pb-1">🚗 Informations véhicule</h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Immatriculation</Label>
                <Input value={vehicleData.immatriculation || ""} onChange={e => updateVehicle("immatriculation", e.target.value)} placeholder="AB-123-CD" data-testid="v-immat" />
              </div>
              <div>
                <Label className="text-xs">Marque</Label>
                <Input value={vehicleData.marque || ""} onChange={e => updateVehicle("marque", e.target.value)} placeholder="Renault" data-testid="v-marque" />
              </div>
              <div>
                <Label className="text-xs">Modèle</Label>
                <Input value={vehicleData.modele || ""} onChange={e => updateVehicle("modele", e.target.value)} placeholder="Clio" data-testid="v-modele" />
              </div>
              <div>
                <Label className="text-xs">Version</Label>
                <Input value={vehicleData.version || ""} onChange={e => updateVehicle("version", e.target.value)} placeholder="TCe 100" />
              </div>
              <div>
                <Label className="text-xs">Année</Label>
                <Input type="number" value={vehicleData.annee || ""} onChange={e => updateVehicle("annee", e.target.value)} placeholder="2024" />
              </div>
              <div>
                <Label className="text-xs">Énergie</Label>
                <Select value={vehicleData.energie || ""} onValueChange={v => updateVehicle("energie", v)}>
                  <SelectTrigger><SelectValue placeholder="Choisir..." /></SelectTrigger>
                  <SelectContent>
                    {ENERGY_OPTIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Kilométrage actuel</Label>
                <Input type="number" value={vehicleData.kilometrage || ""} onChange={e => updateVehicle("kilometrage", e.target.value)} placeholder="15000" />
              </div>
              <div>
                <Label className="text-xs">Puissance fiscale (CV)</Label>
                <Input type="number" value={vehicleData.puissance_fiscale || ""} onChange={e => updateVehicle("puissance_fiscale", e.target.value)} placeholder="7" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">VIN</Label>
                <Input value={vehicleData.vin || ""} onChange={e => updateVehicle("vin", e.target.value)} placeholder="VF1BB0F0F80028746" />
              </div>
            </div>
          </div>
        )}

        {/* Section Contrat véhicule */}
        <div className="space-y-3">
          <h4 className="font-semibold text-sm text-foreground border-b pb-1">📄 Contrat véhicule</h4>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Type de contrat</Label>
              <Select value={contractData.type_contrat_auto || ""} onValueChange={v => updateContract("type_contrat_auto", v)}>
                <SelectTrigger data-testid="c-type"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                <SelectContent>
                  {CONTRACT_TYPE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Loueur / Assureur</Label>
              <Input value={contractData.loueur_ou_assureur || ""} onChange={e => updateContract("loueur_ou_assureur", e.target.value)} placeholder="Arval / AXA..." data-testid="c-loueur" />
            </div>
            <div>
              <Label className="text-xs">Durée (mois)</Label>
              <Input type="number" value={contractData.duree_mois || ""} onChange={e => updateContract("duree_mois", e.target.value)} placeholder="48" />
            </div>
            <div>
              <Label className="text-xs">Loyer mensuel HT</Label>
              <Input type="number" value={contractData.monthly_rent_ht || ""} onChange={e => updateContract("monthly_rent_ht", e.target.value)} placeholder="350" />
            </div>
            <div>
              <Label className="text-xs">Dépôt de garantie</Label>
              <Input type="number" value={contractData.depot_garantie || ""} onChange={e => updateContract("depot_garantie", e.target.value)} placeholder="0" />
            </div>
            <div>
              <Label className="text-xs">Valeur de rachat</Label>
              <Input type="number" value={contractData.valeur_rachat || ""} onChange={e => updateContract("valeur_rachat", e.target.value)} placeholder="5000" />
            </div>
            <div>
              <Label className="text-xs">Km annuel autorisé</Label>
              <Input type="number" value={contractData.km_annuel_autorise || ""} onChange={e => updateContract("km_annuel_autorise", e.target.value)} placeholder="20000" />
            </div>
            <div>
              <Label className="text-xs">Coût km supplémentaire (€/km)</Label>
              <Input type="number" step="0.01" value={contractData.cout_km_supplementaire || ""} onChange={e => updateContract("cout_km_supplementaire", e.target.value)} placeholder="0.15" />
            </div>
            {(contractData.type_contrat_auto === "assurance" || contractData.type_contrat_auto === "maintenance") && (
              <>
                <div>
                  <Label className="text-xs">Franchise sinistre</Label>
                  <Input type="number" value={contractData.franchise_sinistre || ""} onChange={e => updateContract("franchise_sinistre", e.target.value)} placeholder="500" />
                </div>
                <div>
                  <Label className="text-xs">Plafond indemnisation</Label>
                  <Input type="number" value={contractData.plafond_indemnisation || ""} onChange={e => updateContract("plafond_indemnisation", e.target.value)} placeholder="50000" />
                </div>
              </>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => { onSkip?.(); onOpenChange(false); }} disabled={loading} data-testid="skip-vehicle-link">
            Ignorer
          </Button>
          <Button onClick={handleConfirm} disabled={loading} className="gap-2" data-testid="confirm-vehicle-link">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Car className="w-4 h-4" />}
            {loading ? "Création..." : "Valider & créer dans Parc Auto"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
